import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PincodeInput } from './PincodeInput';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { MapPin, AlertTriangle, Crosshair, Link as LinkIcon, Loader2, CheckCircle2 } from 'lucide-react';

const LocationsSettings = () => {
  const { user, apiCall } = useAuth();
  const { toast } = useToast();
  
  const [locations, setLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locationLimit, setLocationLimit] = useState({ limit: 10, current: 0, remaining: 10 });

  // iter-231-M — Vendor Location Confirmation UI state.
  // Holds resolved geo info (lat/lon/source) for the badge next to the
  // pincode input, plus per-control loading flags so the buttons can
  // spin without re-rendering the rest of the form.
  const [geoInfo, setGeoInfo] = useState(null); // { latitude, longitude, geo_source }
  const [geoLoading, setGeoLoading] = useState({ current: false, url: false });
  const [mapsUrl, setMapsUrl] = useState('');
  
  const [locationForm, setLocationForm] = useState({
    location_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    phone: '',
    email: '',
    serviceable_area_type: 'radius',
    serviceable_radius_km: '',
    serviceable_pincodes: '',
    serviceable_cities: '',
    serviceable_states: '',
    serviceable_countries: '',
    is_primary: false,
    is_active: true
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const [locationsData, limitData] = await Promise.all([
        apiCall('/api/vendor-locations/'),
        apiCall('/api/vendor-locations/limit')
      ]);
      setLocations(locationsData);
      setLocationLimit(limitData);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setGeoInfo(null);
    setMapsUrl('');
    setLocationForm({
      location_name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'India',
      phone: '',
      email: '',
      serviceable_area_type: 'radius',
      serviceable_radius_km: '',
      serviceable_pincodes: '',
      serviceable_cities: '',
      serviceable_states: '',
      serviceable_countries: '',
      is_primary: locations.length === 0,
      is_active: true
    });
    setShowLocationModal(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setGeoInfo(
      (location.latitude && location.longitude)
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
            geo_source: location.geo_source || 'saved',
          }
        : null
    );
    setMapsUrl(location.google_maps_url || '');
    setLocationForm({
      ...location,
      // Default serviceable_area_type to 'radius' if not set (legacy locations)
      serviceable_area_type: location.serviceable_area_type || 'radius',
      serviceable_radius_km: location.serviceable_radius_km || '',
      serviceable_pincodes: Array.isArray(location.serviceable_pincodes) 
        ? location.serviceable_pincodes.join(', ') 
        : location.serviceable_pincodes || '',
      serviceable_cities: Array.isArray(location.serviceable_cities) 
        ? location.serviceable_cities.join(', ') 
        : location.serviceable_cities || '',
      serviceable_states: Array.isArray(location.serviceable_states) 
        ? location.serviceable_states.join(', ') 
        : location.serviceable_states || '',
      serviceable_countries: Array.isArray(location.serviceable_countries) 
        ? location.serviceable_countries.join(', ') 
        : location.serviceable_countries || ''
    });
    setShowLocationModal(true);
  };

  // iter-231-M — Location Confirmation helpers (Use Current Location +
  // Paste Google Maps URL). Both flow through the SAME backend resolver
  // (`/api/vendor-geo/resolve`) so coordinates are stamped consistently.
  // Reverse-geocoding backfills address fields when the vendor confirms.
  const applyResolved = (data, source) => {
    setGeoInfo({
      latitude: data.latitude,
      longitude: data.longitude,
      geo_source: data.geo_source || source,
    });
    if (data.address) {
      setLocationForm((prev) => ({
        ...prev,
        // Only overwrite blank fields so the vendor doesn't lose
        // anything they already typed.
        address_line1: prev.address_line1 || data.address.address_line1 || '',
        address_line2: prev.address_line2 || data.address.address_line2 || '',
        city:          prev.city          || data.address.city          || '',
        state:         prev.state         || data.address.state         || '',
        postal_code:   prev.postal_code   || data.address.postal_code   || '',
        country:       prev.country       || data.address.country       || 'India',
        latitude:      data.latitude,
        longitude:     data.longitude,
        google_maps_url: prev.google_maps_url || (source === 'google_maps_url' ? mapsUrl : prev.google_maps_url),
      }));
    } else {
      setLocationForm((prev) => ({
        ...prev,
        latitude: data.latitude,
        longitude: data.longitude,
      }));
    }
  };

  const handleUseCurrentLocation = () => {
    if (!('geolocation' in navigator)) {
      toast({ title: 'Not supported', description: 'Your browser does not support geolocation.', variant: 'destructive' });
      return;
    }
    setGeoLoading((s) => ({ ...s, current: true }));
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const data = await apiCall('/api/vendor-geo/resolve', {
            method: 'POST',
            body: JSON.stringify({
              browser_lat: pos.coords.latitude,
              browser_lng: pos.coords.longitude,
              reverse: true,
            }),
          });
          if (data?.geo_verified) {
            applyResolved(data, 'browser_location');
            toast({ title: 'Location captured', description: 'Address fields prefilled — review and confirm.' });
          } else {
            toast({ title: 'Could not resolve', description: 'We got your coordinates but reverse-geocoding failed. Please type the address.', variant: 'destructive' });
          }
        } catch (err) {
          toast({ title: 'Resolve failed', description: err.message || 'Could not resolve current location.', variant: 'destructive' });
        } finally {
          setGeoLoading((s) => ({ ...s, current: false }));
        }
      },
      (err) => {
        setGeoLoading((s) => ({ ...s, current: false }));
        toast({
          title: 'Permission denied',
          description: err?.message || 'Please allow location access to use this feature.',
          variant: 'destructive',
        });
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 }
    );
  };

  const handleResolveMapsUrl = async () => {
    if (!mapsUrl?.trim()) {
      toast({ title: 'Paste a URL', description: 'Paste a Google Maps share link first.', variant: 'destructive' });
      return;
    }
    setGeoLoading((s) => ({ ...s, url: true }));
    try {
      const data = await apiCall('/api/vendor-geo/resolve', {
        method: 'POST',
        body: JSON.stringify({ google_maps_url: mapsUrl.trim(), reverse: true }),
      });
      if (data?.geo_verified) {
        applyResolved(data, 'google_maps_url');
        toast({ title: 'Coordinates extracted', description: 'Address fields prefilled from the map pin — review and confirm.' });
      } else {
        toast({ title: 'No coordinates found', description: 'That URL did not contain a recognisable lat/lng. Try a different link.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Resolve failed', description: err.message || 'Could not parse the URL.', variant: 'destructive' });
    } finally {
      setGeoLoading((s) => ({ ...s, url: false }));
    }
  };

  const handleSaveLocation = async (e) => {
    e.preventDefault();
    
    // Client-side validation with user-friendly messages
    if (!locationForm.location_name?.trim()) {
      toast({ title: 'Missing Field', description: 'Please enter a location name.', variant: 'destructive' });
      return;
    }
    if (!locationForm.postal_code?.trim()) {
      toast({ title: 'Missing Field', description: 'Please enter a postal/pin code.', variant: 'destructive' });
      return;
    }
    if (!locationForm.city?.trim()) {
      toast({ title: 'Missing Field', description: 'Please enter a city.', variant: 'destructive' });
      return;
    }
    
    // Validate serviceable area based on type
    if (locationForm.serviceable_area_type === 'radius') {
      const radius = parseFloat(locationForm.serviceable_radius_km);
      if (!radius || radius <= 0) {
        toast({ 
          title: 'Set Delivery Radius', 
          description: 'Please set a delivery radius (in KM) before saving the location. This defines how far you can deliver from this location.',
          variant: 'destructive' 
        });
        return;
      }
    } else if (locationForm.serviceable_area_type === 'pincodes' && !locationForm.serviceable_pincodes?.trim()) {
      toast({ title: 'Set Serviceable Pincodes', description: 'Please enter at least one pincode you can deliver to.', variant: 'destructive' });
      return;
    } else if (locationForm.serviceable_area_type === 'cities' && !locationForm.serviceable_cities?.trim()) {
      toast({ title: 'Set Serviceable Cities', description: 'Please enter at least one city you can deliver to.', variant: 'destructive' });
      return;
    }
    
    setLoading(true);

    try {
      const locationData = {
        ...locationForm,
        // Convert empty strings to null for numeric fields
        serviceable_radius_km: locationForm.serviceable_radius_km 
          ? parseFloat(locationForm.serviceable_radius_km) 
          : null,
        serviceable_pincodes: locationForm.serviceable_pincodes 
          ? locationForm.serviceable_pincodes.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        serviceable_cities: locationForm.serviceable_cities 
          ? locationForm.serviceable_cities.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        serviceable_states: locationForm.serviceable_states 
          ? locationForm.serviceable_states.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        serviceable_countries: locationForm.serviceable_countries 
          ? locationForm.serviceable_countries.split(',').map(s => s.trim()).filter(Boolean)
          : []
      };

      if (editingLocation) {
        await apiCall(`/api/vendor-locations/${editingLocation.id}`, {
          method: 'PUT',
          body: JSON.stringify(locationData)
        });
        toast({ title: 'Success', description: 'Location updated successfully' });
      } else {
        await apiCall('/api/vendor-locations/', {
          method: 'POST',
          body: JSON.stringify(locationData)
        });
        toast({ title: 'Success', description: 'Location added successfully' });
      }

      setShowLocationModal(false);
      fetchLocations();
    } catch (error) {
      console.error('Location save error:', error);
      // Parse Pydantic validation errors into human-readable messages
      let errorMessage = 'Failed to save location. Please check all fields and try again.';
      try {
        const detail = error.response?.data?.detail || error.message || '';
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          const fieldMessages = detail.map(err => {
            const field = (err.loc || []).slice(-1)[0] || 'field';
            const friendlyNames = {
              'serviceable_radius_km': 'Delivery Radius',
              'postal_code': 'Pin Code',
              'location_name': 'Location Name',
              'city': 'City',
              'state': 'State',
            };
            return friendlyNames[field] || field;
          });
          errorMessage = `Please fill in: ${fieldMessages.join(', ')}`;
        }
      } catch { /* use default message */ }
      toast({
        title: 'Could not save location',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!confirm('Are you sure you want to delete this location?')) return;

    try {
      await apiCall(`/api/vendor-locations/${locationId}`, { method: 'DELETE' });
      toast({ title: 'Success', description: 'Location deleted successfully' });
      fetchLocations();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete location',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Locations</h2>
        <p className="text-gray-500 mt-2">Manage your physical store or business locations</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>Store Locations</CardTitle>
              <CardDescription>Manage your physical store or business locations</CardDescription>
            </div>
            <Button 
              onClick={handleAddLocation}
              disabled={locationLimit.current >= locationLimit.limit}
              className="w-full sm:w-auto"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Add Location
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Location Limit Badge */}
            <div className={`border rounded-lg p-4 ${
              locationLimit.remaining === 0 ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`h-5 w-5 ${
                    locationLimit.remaining === 0 ? 'text-orange-600' : 'text-blue-600'
                  }`} />
                  <div>
                    <p className="text-sm font-medium">Location Usage</p>
                    <p className="text-sm">
                      <strong>{locationLimit.current} of {locationLimit.limit}</strong> locations used
                    </p>
                  </div>
                </div>
                {locationLimit.remaining === 0 && (
                  <Button variant="outline" size="sm">
                    Upgrade Plan
                  </Button>
                )}
              </div>
            </div>

            {/* Locations List */}
            {locations.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No locations added yet</p>
                <p className="text-sm text-gray-400 mb-4">Add your first store location to get started</p>
                <Button onClick={handleAddLocation} variant="outline">
                  <MapPin className="h-4 w-4 mr-2" />
                  Add First Location
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {locations.map((location) => (
                  <Card key={location.id}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold">{location.location_name}</h3>
                            {location.is_primary && (
                              <Badge variant="default">Primary</Badge>
                            )}
                            {!location.is_active && (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>{location.address_line1}</p>
                            {location.address_line2 && <p>{location.address_line2}</p>}
                            <p>{location.city}, {location.state} {location.postal_code}</p>
                            <p>{location.country}</p>
                            {location.phone && (
                              <p className="flex items-center gap-2 mt-2">
                                <span className="font-medium">Phone:</span> {location.phone}
                              </p>
                            )}
                            {location.email && (
                              <p className="flex items-center gap-2">
                                <span className="font-medium">Email:</span> {location.email}
                              </p>
                            )}
                            {location.serviceable_area_type && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-sm font-medium text-gray-700 mb-1">Serviceable Area:</p>
                                {location.serviceable_area_type === 'radius' && location.serviceable_radius_km && (
                                  <Badge variant="outline" className="text-xs">
                                    Within {location.serviceable_radius_km} KM radius
                                  </Badge>
                                )}
                                {location.serviceable_area_type === 'pincodes' && location.serviceable_pincodes?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {location.serviceable_pincodes.slice(0, 5).map((code, idx) => (
                                      <Badge key={code.id || code.name || `${code.title || "code"}-${idx}`} variant="outline" className="text-xs">{code}</Badge>
                                    ))}
                                    {location.serviceable_pincodes.length > 5 && (
                                      <Badge variant="outline" className="text-xs">
                                        +{location.serviceable_pincodes.length - 5} more
                                      </Badge>
                                    )}
                                  </div>
                                )}
                                {location.serviceable_area_type === 'cities' && location.serviceable_cities?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {location.serviceable_cities.slice(0, 3).map((city, idx) => (
                                      <Badge key={city.id || city.name || `${city.title || "city"}-${idx}`} variant="outline" className="text-xs">{city}</Badge>
                                    ))}
                                    {location.serviceable_cities.length > 3 && (
                                      <Badge variant="outline" className="text-xs">
                                        +{location.serviceable_cities.length - 3} more cities
                                      </Badge>
                                    )}
                                  </div>
                                )}
                                {location.serviceable_area_type === 'states' && location.serviceable_states?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {location.serviceable_states.map((state, idx) => (
                                      <Badge key={state.id || state.name || `${state.title || "state"}-${idx}`} variant="outline" className="text-xs">{state}</Badge>
                                    ))}
                                  </div>
                                )}
                                {location.serviceable_area_type === 'countries' && location.serviceable_countries?.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {location.serviceable_countries.map((country, idx) => (
                                      <Badge key={country.id || country.name || `${country.title || "country"}-${idx}`} variant="outline" className="text-xs">{country}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 sm:ml-4 mt-4 sm:mt-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditLocation(location)}
                            className="flex-1 sm:flex-none"
                          >
                            Edit
                          </Button>
                          {!location.is_primary && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteLocation(location.id)}
                              className="text-red-600 hover:text-red-700 flex-1 sm:flex-none"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSaveLocation}>
              <div className="p-4 sm:p-6 border-b">
                <h2 className="text-xl font-semibold">
                  {editingLocation ? 'Edit Location' : 'Add New Location'}
                </h2>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                {/* iter-231-M — Vendor Location Confirmation controls.
                    Two shortcuts to skip typing: (a) Use Current Location
                    (browser geolocation + reverse-geocode), (b) Paste a
                    Google Maps share link (extract lat/lng + reverse-
                    geocode). Both prefill the address fields below
                    without overwriting anything the vendor already typed. */}
                <div className="rounded-lg border bg-slate-50 p-3 space-y-3" data-testid="vendor-geo-confirm-card">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <MapPin className="h-4 w-4" />
                    Confirm your location
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleUseCurrentLocation}
                      disabled={geoLoading.current}
                      className="flex-1 sm:flex-initial"
                      data-testid="use-current-location-btn"
                    >
                      {geoLoading.current ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Crosshair className="h-4 w-4 mr-2" />
                      )}
                      Use current location
                    </Button>
                    <div className="flex-1 flex gap-2">
                      <Input
                        type="url"
                        placeholder="Paste Google Maps URL"
                        value={mapsUrl}
                        onChange={(e) => setMapsUrl(e.target.value)}
                        data-testid="google-maps-url-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleResolveMapsUrl}
                        disabled={geoLoading.url || !mapsUrl?.trim()}
                        data-testid="resolve-maps-url-btn"
                      >
                        {geoLoading.url ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <LinkIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  {geoInfo && geoInfo.latitude != null && geoInfo.longitude != null && (
                    <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="vendor-geo-confirm-badge">
                      <CheckCircle2 className="h-4 w-4" />
                      Coordinates set: {Number(geoInfo.latitude).toFixed(4)}, {Number(geoInfo.longitude).toFixed(4)}
                      <Badge variant="outline" className="text-emerald-700 border-emerald-200 text-[10px] uppercase">
                        {geoInfo.geo_source?.replace(/_/g, ' ') || 'verified'}
                      </Badge>
                    </div>
                  )}
                  <p className="text-xs text-slate-500">
                    Or type the address below — it will be geocoded when you save.
                  </p>
                </div>

                <div>
                  <Label htmlFor="location_name">Location Name *</Label>
                  <Input
                    id="location_name"
                    value={locationForm.location_name}
                    onChange={(e) => setLocationForm({...locationForm, location_name: e.target.value})}
                    placeholder="e.g., Downtown Store, Main Branch"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="address_line1">Address Line 1 *</Label>
                  <Input
                    id="address_line1"
                    value={locationForm.address_line1}
                    onChange={(e) => setLocationForm({...locationForm, address_line1: e.target.value})}
                    placeholder="Street address"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    value={locationForm.address_line2}
                    onChange={(e) => setLocationForm({...locationForm, address_line2: e.target.value})}
                    placeholder="Apartment, suite, unit, etc. (optional)"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <PincodeInput
                      id="postal_code"
                      label="Postal / Pin Code *"
                      value={locationForm.postal_code}
                      onChange={(v) => setLocationForm({...locationForm, postal_code: v})}
                      onAutoFill={({ city, state, country }) => {
                        setLocationForm(prev => ({
                          ...prev,
                          ...(city ? { city } : {}),
                          ...(state ? { state } : {}),
                          ...(country ? { country } : {}),
                        }));
                      }}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={locationForm.city}
                      onChange={(e) => setLocationForm({...locationForm, city: e.target.value})}
                      placeholder="Auto-filled from postal code"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state">State / Province *</Label>
                    <Input
                      id="state"
                      value={locationForm.state}
                      onChange={(e) => setLocationForm({...locationForm, state: e.target.value})}
                      placeholder="Auto-filled from postal code"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={locationForm.country}
                      onChange={(e) => setLocationForm({...locationForm, country: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={locationForm.phone}
                      onChange={(e) => setLocationForm({...locationForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={locationForm.email}
                      onChange={(e) => setLocationForm({...locationForm, email: e.target.value})}
                      placeholder="store@example.com"
                    />
                  </div>
                </div>

                {/* Serviceable Area */}
                <div className="border-t pt-4 space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Serviceable Area</Label>
                    <p className="text-sm text-gray-500">Define where you can provide service from this location</p>
                  </div>

                  <div>
                    <Label>Service Area Type</Label>
                    <Select 
                      value={locationForm.serviceable_area_type}
                      onValueChange={(value) => setLocationForm({...locationForm, serviceable_area_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="radius">Radius (Kilometers)</SelectItem>
                        <SelectItem value="pincodes">Postal/Pin Codes</SelectItem>
                        <SelectItem value="cities">Cities</SelectItem>
                        <SelectItem value="states">States/Provinces</SelectItem>
                        <SelectItem value="countries">Countries</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {locationForm.serviceable_area_type === 'radius' && (
                    <div>
                      <Label htmlFor="radius" className="flex items-center gap-1">
                        Service Radius (KM) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="radius"
                        type="number"
                        step="0.1"
                        min="1"
                        value={locationForm.serviceable_radius_km}
                        onChange={(e) => setLocationForm({...locationForm, serviceable_radius_km: e.target.value})}
                        placeholder="e.g., 25"
                        data-testid="radius-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        How far (in KM) can you deliver from this location? This is required to enable deliveries.
                      </p>
                    </div>
                  )}

                  {locationForm.serviceable_area_type === 'pincodes' && (
                    <div>
                      <Label htmlFor="pincodes">Postal/Pin Codes</Label>
                      <Textarea
                        id="pincodes"
                        value={locationForm.serviceable_pincodes}
                        onChange={(e) => setLocationForm({...locationForm, serviceable_pincodes: e.target.value})}
                        placeholder="Enter codes separated by commas (e.g., 110001, 110002, 110003)"
                        rows={3}
                      />
                    </div>
                  )}

                  {locationForm.serviceable_area_type === 'cities' && (
                    <div>
                      <Label htmlFor="cities">Cities</Label>
                      <Textarea
                        id="cities"
                        value={locationForm.serviceable_cities}
                        onChange={(e) => setLocationForm({...locationForm, serviceable_cities: e.target.value})}
                        placeholder="Enter cities separated by commas (e.g., New York, Boston, Chicago)"
                        rows={3}
                      />
                    </div>
                  )}

                  {locationForm.serviceable_area_type === 'states' && (
                    <div>
                      <Label htmlFor="states">States/Provinces</Label>
                      <Textarea
                        id="states"
                        value={locationForm.serviceable_states}
                        onChange={(e) => setLocationForm({...locationForm, serviceable_states: e.target.value})}
                        placeholder="Enter states separated by commas (e.g., California, Texas, Florida)"
                        rows={3}
                      />
                    </div>
                  )}

                  {locationForm.serviceable_area_type === 'countries' && (
                    <div>
                      <Label htmlFor="countries">Countries</Label>
                      <Textarea
                        id="countries"
                        value={locationForm.serviceable_countries}
                        onChange={(e) => setLocationForm({...locationForm, serviceable_countries: e.target.value})}
                        placeholder="Enter countries separated by commas (e.g., United States, Canada, Mexico)"
                        rows={3}
                      />
                    </div>
                  )}
                </div>

                {locations.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_primary"
                      checked={locationForm.is_primary}
                      onChange={(e) => setLocationForm({...locationForm, is_primary: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="is_primary" className="font-normal">
                      Set as primary location
                    </Label>
                  </div>
                )}
              </div>
              <div className="p-6 border-t flex gap-3">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : editingLocation ? 'Update Location' : 'Add Location'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowLocationModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsSettings;
